import moment from "moment";
import {
  AssessmentGroupAnonymity,
  AssessmentGroupDelivery,
  AssessmentGroupProviders,
  AssessmentQuestionResponses,
  AssessmentTemplateComplianceRequirement,
  AssessmentType,
  CompetencyAssessmentType,
  ComplianceProgramCalibrationType,
  GetComplianceProgramAdminQuery,
} from "types/graphql-schema";

import { ProgramEvaluationPeriodType, ProgramForm } from "./types";

export const defaultAssessmentTemplateSettings = (
  assessmentType: AssessmentType,
): AssessmentTemplateComplianceRequirement => ({
  assessmentType,
  questionSetIds: [],
  anonymity: AssessmentGroupAnonymity.NotAnonymous,
  providers: AssessmentGroupProviders.Default,
  delivery: AssessmentGroupDelivery.None,
  enableRoleBasedAssessments: false,
  enableGoalAssessments: false,
  enableCoreValueAssessments: false,
  roleBasedAssessmentsAssessNextRole: false,
  roleBasedAssessmentsSelfAssessNextRole: false,
  competencyAssessmentType: CompetencyAssessmentType.CriteriaCombined,
  nextRoleCompetencyAssessmentType: CompetencyAssessmentType.CriteriaCombined,
  roleBasedAssessmentScale: 3,
  roleBasedAssessmentScaleStartsAt: 1,
  roleBasedAssessmentScaleLabels: ["", "", ""],
  goalAssessmentScale: 3,
  goalAssessmentScaleStartsAt: 1,
  goalAssessmentScaleLabels: ["", "", ""],
  goalAssessmentResponses: AssessmentQuestionResponses.ExcludeSelfAssessment,
  coreValueAssessmentScale: 3,
  coreValueAssessmentScaleStartsAt: 1,
  coreValueAssessmentScaleLabels: ["", "", ""],
  coreValueAssessmentResponses:
    AssessmentQuestionResponses.ExcludeSelfAssessment,
});

export const getEvaluationPeriodType = (
  complianceProgram: GetComplianceProgramAdminQuery["complianceProgram"],
): ProgramForm["evaluationPeriod"] => {
  const defaultType = {
    type: ProgramEvaluationPeriodType.PAST_HALF_YEAR,
  };
  if (!complianceProgram) {
    return defaultType;
  }

  const { periodEndDate, periodStartDate } = complianceProgram;
  if (!periodEndDate || !periodStartDate) {
    return defaultType;
  }

  const weeksDiff = moment(periodEndDate).diff(periodStartDate, "week");
  if (weeksDiff === 1) {
    return {
      type: ProgramEvaluationPeriodType.PAST_WEEK,
    };
  }
  if (weeksDiff === 2) {
    return {
      type: ProgramEvaluationPeriodType.PAST_TWO_WEEKS,
    };
  }

  const monthsDiff = moment(periodEndDate).diff(periodStartDate, "month");
  if (monthsDiff === 1) {
    return {
      type: ProgramEvaluationPeriodType.PAST_MONTH,
    };
  }
  if (monthsDiff === 2) {
    return {
      type: ProgramEvaluationPeriodType.PAST_TWO_MONTHS,
    };
  }
  if (monthsDiff === 3) {
    return {
      type: ProgramEvaluationPeriodType.PAST_QUARTER,
    };
  }
  if (monthsDiff === 6) {
    return {
      type: ProgramEvaluationPeriodType.PAST_HALF_YEAR,
    };
  }

  const yearsDiff = moment(periodEndDate).diff(periodStartDate, "year");
  if (yearsDiff === 1) {
    return {
      type: ProgramEvaluationPeriodType.PAST_YEAR,
    };
  }

  return {
    type: ProgramEvaluationPeriodType.CUSTOM,
    startDate: periodStartDate,
    endDate: periodEndDate,
  };
};

export const convertEvaluationPeriodTypeToPeriod = (
  evaluationPeriod: ProgramForm["evaluationPeriod"],
  startDate?: string | null,
): {
  periodStartDate?: string;
  periodEndDate?: string;
} => {
  if (!startDate) {
    return {};
  }

  switch (evaluationPeriod.type) {
    case ProgramEvaluationPeriodType.PAST_WEEK:
      return {
        periodStartDate: moment(startDate)
          .subtract(1, "week")
          .format("YYYY-MM-DD"),
        periodEndDate: startDate,
      };
    case ProgramEvaluationPeriodType.PAST_TWO_WEEKS:
      return {
        periodStartDate: moment(startDate)
          .subtract(2, "week")
          .format("YYYY-MM-DD"),
        periodEndDate: startDate,
      };
    case ProgramEvaluationPeriodType.PAST_MONTH:
      return {
        periodStartDate: moment(startDate)
          .subtract(1, "month")
          .format("YYYY-MM-DD"),
        periodEndDate: startDate,
      };
    case ProgramEvaluationPeriodType.PAST_TWO_MONTHS:
      return {
        periodStartDate: moment(startDate)
          .subtract(2, "month")
          .format("YYYY-MM-DD"),
        periodEndDate: startDate,
      };
    case ProgramEvaluationPeriodType.PAST_QUARTER:
      return {
        periodStartDate: moment(startDate)
          .subtract(3, "month")
          .format("YYYY-MM-DD"),
        periodEndDate: startDate,
      };
    case ProgramEvaluationPeriodType.PAST_HALF_YEAR:
      return {
        periodStartDate: moment(startDate)
          .subtract(6, "month")
          .format("YYYY-MM-DD"),
        periodEndDate: startDate,
      };
    case ProgramEvaluationPeriodType.PAST_YEAR:
      return {
        periodStartDate: moment(startDate)
          .subtract(1, "year")
          .format("YYYY-MM-DD"),
        periodEndDate: startDate,
      };
    case ProgramEvaluationPeriodType.CUSTOM:
      return {
        periodStartDate: evaluationPeriod.startDate,
        periodEndDate: evaluationPeriod.endDate,
      };
  }
};

export const convertDeliveryTypeToDeliveryOption = (
  assessmentTemplates: AssessmentTemplateComplianceRequirement[],
  sharesWithSubject: boolean,
  calibration: ComplianceProgramCalibrationType,
): AssessmentTemplateComplianceRequirement[] => {
  if (!sharesWithSubject) {
    return assessmentTemplates.map((template) => ({
      ...template,
      delivery: AssessmentGroupDelivery.None,
    }));
  }

  if (calibration !== ComplianceProgramCalibrationType.PostCalibration) {
    return assessmentTemplates.map((template) => ({
      ...template,
      delivery: AssessmentGroupDelivery.Partial,
    }));
  }

  return assessmentTemplates.map((template) => ({
    ...template,
    delivery:
      template.assessmentType === AssessmentType.Performance
        ? AssessmentGroupDelivery.AdminApproval
        : AssessmentGroupDelivery.Partial,
  }));
};
